import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  makeStyles, shorthands, tokens, mergeClasses,
  Body1, Caption1, Spinner, Button, Link,
} from '@fluentui/react-components';
import {
  OpenRegular, BranchRegular,
  CalendarLtrRegular, GroupRegular, TagRegular, CommentRegular,
} from '@fluentui/react-icons';
import { useEpicTree, useMyProjects, useWorkItemComments } from '../../api/hooks';
import { HeroProgressMetrics } from '../../components/common/HeroProgressMetrics';
import { brandColors, brandFonts } from '../../theme/brandColors';
import {
  classify, isDone, isRemoved, flatten, storyStats, stripHtml, discoveryStats,
  weightedProgress, progressBasisLabel,
  STATUS_META, STACK_ORDER, type Completion,
} from './EpicProgressPage';
import type { EpicTreeNode } from '../../api/types';

/* FEATURE PROGRESS (drill-down from Feature Reports).
   Mirrors the Epic Progress executive layout but scoped to ONE feature:
   - Navy hero (kicker → feature title + status pill → story-status bars + task-based %).
   - 5 KPI tiles: Stories complete, Story points, Tasks complete, Bugs resolved, Completion %.
   - Left column: a stacked column chart of STORIES (segments by their TASK status, so the
     bar height = task volume and the fill = task completion), and — only when bugs exist —
     a second "Bugs by status" chart underneath.
   - Right column: the SELECTED story's details — sprint, area, tags and discussion comments
     (NOT the technical task list).
   Reuses classify/flatten/storyStats/STATUS_META from EpicProgressPage (one source of truth). */

// ── Local helpers ──
interface TaskBreakdown { counts: Record<Completion, number>; total: number; }
function taskBreakdown(story: EpicTreeNode): TaskBreakdown {
  const counts: Record<Completion, number> = { closed: 0, resolved: 0, inProgress: 0, toDo: 0, removed: 0 };
  flatten(story)
    .filter((n) => n.workItemType === 'Task')
    .forEach((t) => { counts[classify(t.state)]++; });
  const total = counts.closed + counts.resolved + counts.inProgress + counts.toDo + counts.removed;
  return { counts, total };
}

// Azure DevOps area/iteration paths are backslash-delimited (e.g. "Project\\Sprint 3").
// Show the leaf segment for brevity; the full path is kept as the title attribute.
function leaf(path: string | null | undefined): string {
  if (!path) return '';
  const parts = path.split('\\').map((p) => p.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : path.trim();
}
function parseTags(tags: string | null | undefined): string[] {
  if (!tags) return [];
  return tags.split(';').map((t) => t.trim()).filter(Boolean);
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL, width: '100%', fontFamily: brandFonts.body },

  // ── Hero ──
  hero: {
    backgroundColor: brandColors.navy, color: brandColors.white,
    borderRadius: tokens.borderRadiusXLarge, padding: '2.125rem 2.375rem 2rem',
    boxShadow: tokens.shadow28, overflow: 'hidden',
  },
  kicker: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, color: brandColors.lightBlue,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: tokens.spacingVerticalM,
  },
  heroTitleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalL, flexWrap: 'wrap' },
  heroTitle: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero800, lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightBold, letterSpacing: '-0.01em', margin: 0,
  },
  detailMeta: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalM, color: 'rgba(255,255,255,0.7)',
    fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200,
  },
  heroGrid: {
    display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: tokens.spacingHorizontalXXXL, alignItems: 'center',
    marginTop: tokens.spacingVerticalXXL,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr', rowGap: tokens.spacingVerticalXL },
  },
  heroBars: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  barRow: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  barTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  barLabel: { fontSize: tokens.fontSizeBase300, color: 'rgba(255,255,255,0.92)' },
  barVal: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },
  heroTrack: { height: '0.375rem', borderRadius: tokens.borderRadiusCircular, backgroundColor: 'rgba(255,255,255,0.12)', overflow: 'hidden' },
  heroFill: { height: '100%', borderRadius: tokens.borderRadiusCircular },
  composite: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXS,
    borderLeft: '1px solid rgba(255,255,255,0.14)', paddingLeft: tokens.spacingHorizontalXXXL,
    '@media (max-width: 860px)': { borderLeft: 'none', paddingLeft: 0 },
  },
  compositeLabel: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: brandColors.lightBlue,
  },
  compositeValue: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  compositeNum: { fontFamily: brandFonts.heading, fontSize: '3.5rem', fontWeight: tokens.fontWeightBold, lineHeight: '1', color: brandColors.white },
  compositePct: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, color: brandColors.lightBlue },
  compositeMeta: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },

  // ── Pills ──
  pill: {
    display: 'inline-flex', alignItems: 'center', borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  heroPill: {
    backgroundColor: 'rgba(255,255,255,0.14)', color: brandColors.white,
    display: 'inline-flex', alignItems: 'center', borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },

  // ── KPI tiles (5 across) ──
  kpis: {
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: tokens.spacingHorizontalL,
    '@media (max-width: 1080px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
    '@media (max-width: 600px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  kpi: {
    backgroundColor: brandColors.white,
    ...shorthands.border('1px', 'solid', brandColors.greyLine),
    ...shorthands.borderLeft('3px', 'solid', brandColors.blue),
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
  },
  kpiLabel: {
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.12em', textTransform: 'uppercase', color: brandColors.greyText,
  },
  kpiVal: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  kpiNum: { fontFamily: brandFonts.heading, fontSize: '2.375rem', fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, lineHeight: '1', letterSpacing: '-0.02em' },
  kpiUnit: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase300, color: brandColors.greyText },
  // ── Detail grid ──
  detailGrid: {
    // minmax(0, …) (not bare fr) lets the chart column shrink below its content width so the
    // chart scrolls inside its card instead of blowing the grid out past the page.
    display: 'grid', gridTemplateColumns: 'minmax(0, 13fr) minmax(0, 7fr)', gap: tokens.spacingHorizontalXL, alignItems: 'start',
    '@media (max-width: 1080px)': { gridTemplateColumns: '1fr' },
  },
  leftCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL, minWidth: 0 },
  card: {
    backgroundColor: brandColors.white, border: `1px solid ${brandColors.greyLine}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXL}`,
    // min-width:0 lets the card shrink to its column so the chart's overflowX can engage.
    minWidth: 0,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL,
  },
  cardTitle: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, margin: 0 },
  cardHint: { color: brandColors.greyText },

  // ── Stacked column chart ──
  // Many columns keep their width and scroll horizontally inside the chart instead of
  // squashing off the page. overflowX:auto + overflowY:hidden avoids the CSS visible→auto
  // coupling (no stray vertical scrollbar); the hover card is anchored at the TOP of each
  // full-height column so it stays inside the box.
  chart: {
    display: 'flex', alignItems: 'flex-end', gap: tokens.spacingHorizontalM, height: '22rem',
    paddingTop: tokens.spacingVerticalM, overflowX: 'auto', overflowY: 'hidden',
  },
  bugsChart: {
    display: 'flex', alignItems: 'flex-end', gap: tokens.spacingHorizontalM, height: '11rem',
    paddingTop: tokens.spacingVerticalM, overflowX: 'auto', overflowY: 'hidden',
  },
  col: {
    // grow 1 (few columns still fill the chart); shrink 0 + fixed basis/min-width so many
    // columns stay legible and the chart scrolls instead of squashing.
    position: 'relative', flex: '1 0 3rem', minWidth: '3rem', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: tokens.spacingVerticalXS, height: '100%', justifyContent: 'flex-end', cursor: 'pointer',
    '&:hover [role="tooltip"], &:focus-within [role="tooltip"]': { opacity: 1, visibility: 'visible' },
  },
  colStatic: {
    position: 'relative', flex: '1 0 3rem', minWidth: '3rem', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: tokens.spacingVerticalXS, height: '100%', justifyContent: 'flex-end',
  },
  colTrack: { width: '100%', maxWidth: '2.5rem', display: 'flex', flexDirection: 'column-reverse', borderRadius: tokens.borderRadiusSmall, overflow: 'hidden', backgroundColor: tokens.colorNeutralBackground3 },
  colSeg: { width: '100%' },
  hoverCard: {
    // Anchored to the TOP of the (full-height) column so it stays inside the chart's
    // overflow box — see the chart/col notes above re: horizontal scroll.
    position: 'absolute', top: tokens.spacingVerticalS, left: '50%', transform: 'translateX(-50%)',
    zIndex: 9999, minWidth: '13rem', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    backgroundColor: brandColors.navy, color: brandColors.white,
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: tokens.borderRadiusMedium, boxShadow: tokens.shadow16,
    opacity: 0, visibility: 'hidden', transitionProperty: 'opacity', transitionDuration: '0.12s', pointerEvents: 'none',
  },
  hoverTitle: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, lineHeight: tokens.lineHeightBase300 },
  hoverCode: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: 'rgba(255,255,255,0.7)' },
  hoverRows: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, marginTop: tokens.spacingVerticalXS },
  hoverRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, fontSize: tokens.fontSizeBase200 },
  hoverCount: { marginLeft: 'auto', fontFamily: brandFonts.mono, color: 'rgba(255,255,255,0.85)' },
  colLabel: {
    fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: brandColors.greyText,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', textAlign: 'center',
  },
  colSelected: { outline: `2px solid ${brandColors.blue}`, outlineOffset: '2px', borderRadius: tokens.borderRadiusSmall },
  legend: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: tokens.spacingHorizontalL, marginTop: tokens.spacingVerticalS },
  legendItem: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, color: brandColors.greyText, fontSize: tokens.fontSizeBase200 },
  swatch: { width: '0.625rem', height: '0.625rem', borderRadius: tokens.borderRadiusSmall, flexShrink: 0 },

  // ── Right panel: story details ──
  panelHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  panelTitle: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, margin: 0 },
  sectionLabel: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold, letterSpacing: '0.12em', textTransform: 'uppercase', color: brandColors.greyText },
  desc: { color: tokens.colorNeutralForeground1, whiteSpace: 'pre-line', lineHeight: tokens.lineHeightBase400 },
  workItemLink: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS, color: brandColors.blue, flexShrink: 0 },

  storyTrack: { width: '100%', height: '0.3125rem', borderRadius: tokens.borderRadiusCircular, overflow: 'hidden', backgroundColor: brandColors.greyLine },
  storyFill: { height: '100%', borderRadius: tokens.borderRadiusCircular },
  storyFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: brandColors.greyText },

  // Metadata (sprint / area / tags)
  metaList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  metaItem: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  metaKey: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.12em', textTransform: 'uppercase', color: brandColors.greyText,
  },
  metaVal: { color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase300 },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  tag: {
    display: 'inline-flex', alignItems: 'center', borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${brandColors.greyLine}`, backgroundColor: tokens.colorNeutralBackground2,
    color: brandColors.greyText, fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100,
  },

  // Comments
  commentsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  commentList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  comment: {
    border: `1px solid ${brandColors.greyLine}`, borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
  },
  commentTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  commentAuthor: { fontFamily: brandFonts.heading, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy, fontSize: tokens.fontSizeBase200 },
  commentDate: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: brandColors.greyText },
  commentBody: { color: tokens.colorNeutralForeground2, lineHeight: tokens.lineHeightBase300, whiteSpace: 'pre-line', fontSize: tokens.fontSizeBase200 },

  emptyOrError: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalXXXL, textAlign: 'center', color: tokens.colorNeutralForeground2,
  },
});

type Styles = ReturnType<typeof useStyles>;

function WorkItemLink({ url, id, styles }: { url: string | null; id: number; styles: Styles }) {
  if (!url) return null;
  return (
    <Link className={styles.workItemLink} href={url} target="_blank" rel="noopener noreferrer"
      aria-label={`Open work item ${id} in Azure DevOps (opens in a new tab)`}>
      #{id} <OpenRegular fontSize={13} />
    </Link>
  );
}

function SoftPill({ state, styles }: { state: string; styles: Styles }) {
  const c = STATUS_META[classify(state)].color;
  return <span className={styles.pill} style={{ backgroundColor: `${c}29`, color: c }}>{(state || 'Unknown').trim()}</span>;
}

interface StoryDatum {
  story: EpicTreeNode;
  label: string;
  breakdown: TaskBreakdown;
  pct: number;
}

// Stacked-column chart: one column per story, segments by TASK status. Column height
// ∝ task volume; the proportion of "done" segments shows task completion. Stories with
// no tasks render a single floor-height segment in the story's own status colour so the
// column stays visible and selectable.
function StoryChart({ data, selectedId, onSelect, styles }: {
  data: StoryDatum[]; selectedId: number | null; onSelect: (id: number) => void; styles: Styles;
}) {
  const maxTasks = Math.max(1, ...data.map((d) => d.breakdown.total));
  return (
    <>
      <div className={styles.chart}>
        {data.map((d) => {
          const { story, breakdown } = d;
          const hasTasks = breakdown.total > 0;
          const colHeightPct = hasTasks ? (breakdown.total / maxTasks) * 100 : 12;
          const storyColor = STATUS_META[classify(story.state)].color;
          return (
            <div
              key={story.id}
              className={mergeClasses(styles.col, selectedId === story.id && styles.colSelected)}
              onClick={() => onSelect(story.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(story.id); } }}
              role="button" tabIndex={0}
              aria-label={`${d.label}: ${breakdown.total} tasks, ${d.pct}% complete`}
            >
              <div className={styles.hoverCard} role="tooltip">
                <span className={styles.hoverTitle}>{story.customerTitle?.trim() || story.title}</span>
                <span className={styles.hoverCode}>{d.label} · {d.pct}% complete · {breakdown.total} task{breakdown.total === 1 ? '' : 's'}</span>
                <div className={styles.hoverRows}>
                  {hasTasks ? STACK_ORDER.map((k) => (breakdown.counts[k] ? (
                    <div key={k} className={styles.hoverRow}>
                      <span className={styles.swatch} style={{ backgroundColor: STATUS_META[k].color }} aria-hidden />
                      <span>{STATUS_META[k].label}</span>
                      <span className={styles.hoverCount}>{breakdown.counts[k]}</span>
                    </div>
                  ) : null)) : (
                    <div className={styles.hoverRow}>
                      <span className={styles.swatch} style={{ backgroundColor: storyColor }} aria-hidden />
                      <span>No tasks · {STATUS_META[classify(story.state)].label}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.colTrack} style={{ height: `${colHeightPct}%` }}>
                {hasTasks ? STACK_ORDER.map((k) => {
                  const n = breakdown.counts[k];
                  if (!n) return null;
                  return <div key={k} className={styles.colSeg} style={{ height: `${(n / breakdown.total) * 100}%`, backgroundColor: STATUS_META[k].color }} />;
                }) : (
                  <div className={styles.colSeg} style={{ height: '100%', backgroundColor: storyColor }} />
                )}
              </div>
              <span className={styles.colLabel}>{d.label}</span>
            </div>
          );
        })}
      </div>
      <div className={styles.legend}>
        {STACK_ORDER.map((k) => (
          <span key={k} className={styles.legendItem}>
            <span className={styles.swatch} style={{ backgroundColor: STATUS_META[k].color }} aria-hidden />
            {STATUS_META[k].label}
          </span>
        ))}
      </div>
    </>
  );
}

// Bugs-by-status chart — only rendered when the feature has bugs. One column per status
// present, height ∝ count. Static (non-interactive) — it's an at-a-glance quality signal.
function BugsChart({ bugs, styles }: { bugs: EpicTreeNode[]; styles: Styles }) {
  const byStatus = STACK_ORDER.map((k) => ({ k, count: bugs.filter((b) => classify(b.state) === k).length }))
    .filter((x) => x.count > 0);
  const max = Math.max(1, ...byStatus.map((x) => x.count));
  return (
    <div className={styles.bugsChart}>
      {byStatus.map(({ k, count }) => (
        <div key={k} className={styles.colStatic} aria-label={`${STATUS_META[k].label}: ${count} bug${count === 1 ? '' : 's'}`}>
          <div className={styles.colTrack} style={{ height: `${(count / max) * 100}%` }}>
            <div className={styles.colSeg} style={{ height: '100%', backgroundColor: STATUS_META[k].color }} />
          </div>
          <span className={styles.colLabel}>{STATUS_META[k].label} {count}</span>
        </div>
      ))}
    </div>
  );
}

// Right panel — the selected story's details: completion summary, overview, sprint/area/
// tags metadata, and the discussion comments. Deliberately NOT the technical task list.
function StoryDetail({ story, projectId, styles }: { story: EpicTreeNode; projectId: string | undefined; styles: Styles }) {
  const st = storyStats(story);
  const c = STATUS_META[classify(story.state)].color;
  const title = story.customerTitle?.trim() || story.title;
  const desc = stripHtml(story.customerDescription || story.description);
  const tags = parseTags(story.tags);
  const sprint = leaf(story.iterationPath);
  const area = leaf(story.areaPath);

  const { data: comments, isLoading: commentsLoading, error: commentsError } = useWorkItemComments(projectId, story.id);

  return (
    <>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>{title}</h2>
        <WorkItemLink url={story.url} id={story.id} styles={styles} />
      </div>

      <div className={styles.panelHead}>
        <SoftPill state={story.state} styles={styles} />
      </div>

      <div className={styles.storyTrack} role="img" aria-label={`${st.completePct}% of tasks complete`}>
        <div className={styles.storyFill} style={{ width: `${st.completePct}%`, backgroundColor: c }} />
      </div>
      <div className={styles.storyFoot}>
        <span>{st.completePct}% · {st.taskCount > 0 ? `${st.taskDone}/${st.taskCount} tasks` : 'no tasks'}</span>
        {story.storyPoints ? <span>{story.storyPoints} SP</span> : null}
      </div>

      {desc ? (
        <>
          <span className={styles.sectionLabel}>Overview</span>
          <Body1 className={styles.desc}>{desc}</Body1>
        </>
      ) : null}

      <div className={styles.metaList}>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}><CalendarLtrRegular fontSize={13} aria-hidden /> Sprint</span>
          <span className={styles.metaVal} title={story.iterationPath ?? undefined}>{sprint || '—'}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}><GroupRegular fontSize={13} aria-hidden /> Area</span>
          <span className={styles.metaVal} title={story.areaPath ?? undefined}>{area || '—'}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}><TagRegular fontSize={13} aria-hidden /> Tags</span>
          {tags.length ? (
            <div className={styles.tagRow}>
              {tags.map((t) => <span key={t} className={styles.tag}>{t}</span>)}
            </div>
          ) : <span className={styles.metaVal}>—</span>}
        </div>
      </div>

      <div className={styles.commentsHead}>
        <span className={styles.metaKey}><CommentRegular fontSize={13} aria-hidden /> Comments</span>
        <Caption1 className={styles.cardHint}>{comments?.length ?? 0}</Caption1>
      </div>
      {commentsLoading ? (
        <Spinner size="tiny" label="Loading comments…" />
      ) : commentsError ? (
        <Caption1 className={styles.cardHint}>Couldn't load comments.</Caption1>
      ) : comments && comments.length > 0 ? (
        <div className={styles.commentList}>
          {comments.map((cm) => {
            const text = stripHtml(cm.text);
            return (
              <div key={cm.id} className={styles.comment}>
                <div className={styles.commentTop}>
                  <span className={styles.commentAuthor}>{cm.author}</span>
                  <span className={styles.commentDate}>{fmtDate(cm.createdUtc)}</span>
                </div>
                {text ? <Caption1 className={styles.commentBody}>{text}</Caption1> : null}
              </div>
            );
          })}
        </div>
      ) : (
        <Caption1 className={styles.cardHint}>No comments on this story yet.</Caption1>
      )}
    </>
  );
}

export function FeatureProgressPage() {
  const styles = useStyles();
  const { projectId, epicId, featureId } = useParams<{ projectId: string; epicId: string; featureId: string }>();
  const epicIdNum = epicId ? parseInt(epicId, 10) : undefined;
  const featureIdNum = featureId ? parseInt(featureId, 10) : undefined;

  const { data, isLoading, error, refetch } = useEpicTree(projectId, epicIdNum);
  const { data: projects } = useMyProjects();
  const project = projects?.find((p) => p.id === projectId);

  const feature = useMemo(
    () => (data && featureIdNum != null ? flatten(data).find((n) => n.id === featureIdNum && n.workItemType === 'Feature') ?? null : null),
    [data, featureIdNum],
  );

  // Stories under the feature, alphabetical by technical title then id (chart order).
  const sortedStories = useMemo(() => {
    if (!feature) return [] as EpicTreeNode[];
    return flatten(feature)
      .filter((n) => n.workItemType === 'User Story' && n.id !== feature.id)
      .sort((a, b) => a.title.localeCompare(b.title) || (a.id - b.id));
  }, [feature]);

  const bugs = useMemo(
    () => (feature ? flatten(feature).filter((n) => n.workItemType === 'Bug') : []),
    [feature],
  );

  const storyData: StoryDatum[] = useMemo(
    () => sortedStories.map((story) => {
      const breakdown = taskBreakdown(story);
      return { story, label: `#${story.id}`, breakdown, pct: storyStats(story).completePct };
    }),
    [sortedStories],
  );

  const [selectedStoryId, setSelectedStoryId] = useState<number | null>(null);
  useEffect(() => { setSelectedStoryId(null); }, [featureIdNum, epicIdNum]);
  const focusedStoryId = selectedStoryId ?? sortedStories[0]?.id ?? null;
  const focusedStory = useMemo(
    () => sortedStories.find((s) => s.id === focusedStoryId) ?? null,
    [sortedStories, focusedStoryId],
  );

  // ── KPIs / hero metrics ──
  const metrics = useMemo(() => {
    if (!feature) return null;
    const stories = sortedStories;
    const usCounts = { closed: 0, resolved: 0, inProgress: 0, toDo: 0, removed: 0 } as Record<Completion, number>;
    stories.forEach((s) => { usCounts[classify(s.state)]++; });
    const usDone = usCounts.closed + usCounts.resolved;
    const usTotal = stories.length - usCounts.removed;

    let spClosed = 0; let spResolved = 0; let spRemaining = 0;
    stories.forEach((s) => {
      const sp = s.storyPoints ?? 0;
      if (sp <= 0) return;
      const c = classify(s.state);
      if (c === 'removed') return;
      if (c === 'closed') spClosed += sp;
      else if (c === 'resolved') spResolved += sp;
      else spRemaining += sp;
    });
    const spDone = spClosed + spResolved;
    const spTotal = spDone + spRemaining;

    // Closed / Resolved over the feature's user stories (count basis, excluding Removed) — the
    // single shared definition used by the epic pages and the epics list. The headline Progress
    // figure is weighted instead; see `progress` below.
    const pctOf = (us: number) => (usTotal > 0 ? Math.round((us / usTotal) * 100) : 0);
    const pctClosed = pctOf(usCounts.closed);
    const pctResolved = pctOf(usCounts.resolved);

    const allTasks = flatten(feature).filter((n) => n.workItemType === 'Task');
    const tasksDone = allTasks.filter((t) => isDone(classify(t.state))).length;
    const tasksTotal = allTasks.length;

    const bugsDone = bugs.filter((b) => isDone(classify(b.state))).length;
    const bugsTotal = bugs.filter((b) => !isRemoved(b.state)).length;

    const completionPct = tasksTotal > 0
      ? Math.round((tasksDone / tasksTotal) * 100)
      : (usTotal > 0 ? Math.round((usDone / usTotal) * 100) : 0);

    const storyStateBars = [
      { state: 'New', count: usCounts.toDo, color: STATUS_META.toDo.color },
      { state: 'Active', count: usCounts.inProgress, color: STATUS_META.inProgress.color },
      { state: 'Resolved', count: usCounts.resolved, color: STATUS_META.resolved.color },
      { state: 'Closed', count: usCounts.closed, color: STATUS_META.closed.color },
      { state: 'Removed', count: usCounts.removed, color: STATUS_META.removed.color },
    ].filter((b) => b.count > 0);

    return {
      usDone, usTotal, usClosed: usCounts.closed, usResolved: usCounts.resolved,
      spDone, spTotal, spClosed, spResolved,
      pctClosed, pctResolved,
      tasksDone, tasksTotal,
      bugsDone, bugsTotal, completionPct, storyStateBars,
      totalStories: stories.length,
      // Weighted completion over this feature's stories — same formula as the epic page.
      progress: weightedProgress(stories),
    };
  }, [feature, sortedStories, bugs]);

  if (isLoading) return <div className={styles.emptyOrError}><Spinner size="small" label="Loading feature progress…" /></div>;
  if (error) {
    return (
      <div className={styles.emptyOrError}>
        <Body1>Couldn't load feature progress.</Body1>
        <Caption1>{(error as Error).message}</Caption1>
        <Button appearance="primary" onClick={() => refetch()}>Try again</Button>
      </div>
    );
  }
  if (!data) return <div className={styles.emptyOrError}><Body1>Epic not found.</Body1></div>;
  if (!feature || !metrics) return <div className={styles.emptyOrError}><Body1>Feature not found in this epic.</Body1></div>;

  const totalStoryBars = metrics.storyStateBars.reduce((acc, b) => acc + b.count, 0) || 1;
  const d = discoveryStats(feature);

  return (
    <div className={styles.page}>
      {/* Navy hero */}
      <section className={styles.hero}>
        <div className={styles.kicker}><BranchRegular fontSize={14} aria-hidden /> Feature Progress</div>
        <div className={styles.heroTitleRow}>
          <h1 className={styles.heroTitle}>{feature.customerTitle?.trim() || feature.title}</h1>
          <span className={styles.heroPill}>{(feature.state || 'Unknown').trim()}</span>
        </div>
        <div className={styles.detailMeta}>
          {project ? <span>{project.name}</span> : null}
          {feature.customerTitle?.trim() ? <span>· {feature.title}</span> : null}
          <span>· {metrics.totalStories} stor{metrics.totalStories === 1 ? 'y' : 'ies'}</span>
          <span>· {metrics.tasksTotal} task{metrics.tasksTotal === 1 ? '' : 's'}</span>
          {bugs.length ? <span>· {bugs.length} bug{bugs.length === 1 ? '' : 's'}</span> : null}
        </div>

        <div className={styles.heroGrid}>
          <div className={styles.heroBars}>
            {metrics.storyStateBars.map((b) => (
              <div key={b.state} className={styles.barRow}>
                <div className={styles.barTop}>
                  <span className={styles.barLabel}>{b.state}</span>
                  <span className={styles.barVal}>{b.count} · {Math.round((b.count / totalStoryBars) * 100)}%</span>
                </div>
                <div className={styles.heroTrack}>
                  <div className={styles.heroFill} style={{ width: `${(b.count / totalStoryBars) * 100}%`, backgroundColor: b.color }} />
                </div>
              </div>
            ))}
            {metrics.storyStateBars.length === 0 && <span className={styles.barLabel}>No stories under this feature yet.</span>}
          </div>
          <HeroProgressMetrics
            percentProgress={metrics.progress.percent}
            percentClosed={metrics.pctClosed}
            percentResolved={metrics.pctResolved}
            progressMeta={progressBasisLabel(metrics.progress)}
            closedMeta={`${metrics.usClosed} / ${metrics.usTotal} stories`}
            resolvedMeta={`${metrics.usResolved} / ${metrics.usTotal} stories`}
            unit="user stories"
          />
        </div>
      </section>

      {/* KPI tiles */}
      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Stories complete</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{metrics.usDone}</span><span className={styles.kpiUnit}>/ {metrics.usTotal}</span></div>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Story points</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{metrics.spDone}</span><span className={styles.kpiUnit}>/ {metrics.spTotal}</span></div>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Tasks complete</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{metrics.tasksDone}</span><span className={styles.kpiUnit}>/ {metrics.tasksTotal}</span></div>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Bugs resolved</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{metrics.bugsDone}</span><span className={styles.kpiUnit}>/ {metrics.bugsTotal}</span></div>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Completion</span>
          <div className={styles.kpiVal}><span className={styles.kpiNum}>{metrics.completionPct}</span><span className={styles.kpiUnit}>%</span></div>
        </div>
      </div>

      {/* Detail grid */}
      <div className={styles.detailGrid}>
        {/* Left — story chart (+ bugs chart when present) */}
        <div className={styles.leftCol}>
          <section className={styles.card} aria-label="Stories by task completion">
            <h2 className={styles.cardTitle}>Stories by task completion</h2>
            {storyData.length === 0 ? (
              <Caption1 className={styles.cardHint}>No user stories under this feature.</Caption1>
            ) : (
              <>
                <Caption1 className={styles.cardHint}>Each column is a story, split by its task status. Select one to see its details.</Caption1>
                <StoryChart data={storyData} selectedId={focusedStoryId} onSelect={setSelectedStoryId} styles={styles} />
              </>
            )}
          </section>

          {bugs.length > 0 && (
            <section className={styles.card} aria-label="Bugs by status">
              <h2 className={styles.cardTitle}>Bugs</h2>
              <Caption1 className={styles.cardHint}>{bugs.length} bug{bugs.length === 1 ? '' : 's'} across this feature, by status.</Caption1>
              <BugsChart bugs={bugs} styles={styles} />
            </section>
          )}

          {/* New scope — discovery-new work added after sign-off (Doc 29) */}
          <section className={styles.card} aria-label="New scope">
            <h2 className={styles.cardTitle}>New Scope</h2>
            <Caption1 className={styles.cardHint}>
              Work added <strong>after the spec was signed off</strong> — scope legitimately needed to
              build the solution properly — as a share of all work in this feature.
              {d.newCount === 0 ? ' None recorded yet.' : ''}
            </Caption1>
            <div className={styles.kpiVal}>
              <span className={styles.kpiNum}>{d.newPct}</span>
              <span className={styles.kpiUnit}>% · {d.newCount} of {d.total} work items</span>
            </div>
          </section>
        </div>

        {/* Right — selected story details (sprint / area / tags / comments) */}
        <section className={styles.card} aria-label="User story details">
          {focusedStory ? (
            <StoryDetail story={focusedStory} projectId={projectId} styles={styles} />
          ) : (
            <Caption1 className={styles.cardHint}>No user stories under this feature.</Caption1>
          )}
        </section>
      </div>
    </div>
  );
}
